import { Link } from "@tanstack/react-router";
import {
  Building2Icon,
  ChevronDownIcon,
  LogOutIcon,
  SettingsIcon,
  ShieldCheckIcon,
  UserRoundIcon,
} from "lucide-react";

import type { OrganizationSummary, UserSummary } from "../../shared/schemas";
import { OpaqueIdSchema } from "../../shared/schemas";
import { isConsultantUser } from "../lib/organization-context";
import { ShellViewModeSchema, type ShellViewMode } from "./ShellViewModeProvider";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

function UserAvatar({ avatarUrl, initials }: { avatarUrl: string | undefined; initials: string }) {
  return (
    <Avatar className="size-9 rounded-full after:rounded-full after:border-black/10 dark:after:border-white/10">
      {avatarUrl ? (
        <AvatarImage
          alt=""
          className="rounded-full outline -outline-offset-1 outline-black/10 dark:outline-white/10"
          src={avatarUrl}
        />
      ) : null}
      <AvatarFallback className="rounded-full">{initials}</AvatarFallback>
    </Avatar>
  );
}

interface UserProfileMenuProps {
  activeOrganizationId: string | undefined;
  avatarUrl?: string | undefined;
  isOnline: boolean;
  isSigningOut: boolean;
  organizations: OrganizationSummary[];
  user: UserSummary;
  viewMode: ShellViewMode;
  onOrganizationChange: (organizationId: string) => void;
  onSignOut: () => void;
  onViewModeChange: (viewMode: ShellViewMode) => void;
}

function ViewSwitcher({
  value,
  onChange,
}: {
  value: ShellViewMode;
  onChange: (value: ShellViewMode) => void;
}) {
  return (
    <>
      <DropdownMenuSeparator />
      <div className="px-2 pt-1.5 pb-1 font-mono text-[10px] tracking-[0.1em] text-muted-foreground uppercase">
        View as
      </div>
      <DropdownMenuRadioGroup
        aria-label="View as"
        value={value}
        onValueChange={(next: unknown) => {
          const parsed = ShellViewModeSchema.safeParse(next);
          if (parsed.success) onChange(parsed.data);
        }}
      >
        <DropdownMenuRadioItem className="min-h-10" closeOnClick value="admin">
          <ShieldCheckIcon aria-hidden="true" /> Admin view
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem className="min-h-10" closeOnClick value="user">
          <UserRoundIcon aria-hidden="true" /> User view
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </>
  );
}

function WorkspaceSwitcher({
  activeOrganizationId,
  organizations,
  onChange,
}: Pick<UserProfileMenuProps, "activeOrganizationId" | "organizations"> & {
  onChange: (organizationId: string) => void;
}) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="min-h-10 px-2 py-1.5">
        <Building2Icon aria-hidden="true" /> Switch workspace
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-64" side="left" sideOffset={6}>
        <DropdownMenuRadioGroup
          aria-label="Switch workspace"
          value={activeOrganizationId}
          onValueChange={(value: unknown) => {
            const parsed = OpaqueIdSchema.safeParse(value);
            if (parsed.success) onChange(parsed.data);
          }}
        >
          {organizations.map((organization) => (
            <DropdownMenuRadioItem
              className="min-h-10"
              closeOnClick
              key={organization.id}
              value={organization.id}
            >
              {organization.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

function ProfileSummary({
  activeOrganization,
  avatarUrl,
  initials,
  user,
}: {
  activeOrganization: OrganizationSummary | undefined;
  avatarUrl: string | undefined;
  initials: string;
  user: UserSummary;
}) {
  return (
    <div className="flex items-center gap-3 px-2 py-2">
      <UserAvatar avatarUrl={avatarUrl} initials={initials} />
      <div className="min-w-0">
        <p className="m-0 truncate text-sm font-semibold">{user.displayName}</p>
        <p className="m-0 truncate font-mono text-[11px] text-muted-foreground">
          @{user.username}
          {activeOrganization ? ` · ${activeOrganization.name}` : ""}
        </p>
      </div>
    </div>
  );
}

function ProfileMenuItems(
  props: UserProfileMenuProps & {
    activeOrganization: OrganizationSummary | undefined;
    initials: string;
  },
) {
  const consultant = isConsultantUser(props.user);
  return (
    <>
      <ProfileSummary
        activeOrganization={props.activeOrganization}
        avatarUrl={props.avatarUrl}
        initials={props.initials}
        user={props.user}
      />
      <DropdownMenuSeparator />
      <DropdownMenuItem
        className="min-h-10"
        closeOnClick
        render={<Link search={{ org: props.activeOrganizationId }} to="/account" />}
      >
        <SettingsIcon aria-hidden="true" /> Account settings
      </DropdownMenuItem>
      {consultant ? (
        <ViewSwitcher value={props.viewMode} onChange={props.onViewModeChange} />
      ) : null}
      {consultant && props.organizations.length > 1 ? (
        <WorkspaceSwitcher
          activeOrganizationId={props.activeOrganizationId}
          organizations={props.organizations}
          onChange={props.onOrganizationChange}
        />
      ) : null}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        className="min-h-10"
        closeOnClick
        disabled={!props.isOnline || props.isSigningOut}
        onClick={props.onSignOut}
      >
        <LogOutIcon aria-hidden="true" />
        {props.isSigningOut ? "Signing out…" : "Sign out"}
      </DropdownMenuItem>
    </>
  );
}

export function UserProfileMenu({
  activeOrganizationId,
  avatarUrl,
  isOnline,
  isSigningOut,
  organizations,
  user,
  viewMode,
  onOrganizationChange,
  onSignOut,
  onViewModeChange,
}: UserProfileMenuProps) {
  const initials = user.displayName
    .split(/\s+/u)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  const activeOrganization = organizations.find(
    (organization) => organization.id === activeOrganizationId,
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        closeDelay={180}
        delay={0}
        openOnHover
        render={
          <Button
            aria-label={`Open account menu for ${user.displayName}`}
            className="profile-menu-trigger min-h-10 gap-2 px-1.5 py-0 pr-2.5"
            type="button"
            variant="ghost"
          />
        }
      >
        <UserAvatar avatarUrl={avatarUrl} initials={initials} />
        <span className="profile-menu-name max-w-44 truncate text-sm font-semibold">
          {user.displayName}
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          className="profile-menu-chevron size-3.5 text-muted-foreground"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" sideOffset={6}>
        <ProfileMenuItems
          activeOrganization={activeOrganization}
          activeOrganizationId={activeOrganizationId}
          avatarUrl={avatarUrl}
          initials={initials}
          isOnline={isOnline}
          isSigningOut={isSigningOut}
          organizations={organizations}
          user={user}
          viewMode={viewMode}
          onOrganizationChange={onOrganizationChange}
          onSignOut={onSignOut}
          onViewModeChange={onViewModeChange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
